import { useEffect, useRef, useState } from "react";
import { SwitchCamera, Wand2, Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BEAUTY_DEFAULTS, createBeautyPipeline } from "@/lib/beautyEngine";
import BeautyTray from "@/components/BeautyTray";

const isPermissionError = (e) =>
  e?.code === "PERMISSION_DENIED" || e?.name === "NotAllowedError" || /permission denied|not allowed/i.test(e?.message || "");

let joinLock = Promise.resolve();

// Single swappable video layer: real Agora publishing/viewing lives here.
export default function AgoraPlayer({ agora, isHost, startMicMuted = false, initialBeauty = null, initialFilter = "none", initialMirror = true, initialSticker = "none", initialMakeup = "none", frameStyle = null }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("connecting");
  const [retryKey, setRetryKey] = useState(0);
  const [params, setParams] = useState({ ...BEAUTY_DEFAULTS, ...(initialBeauty || {}) });
  const [presetKey, setPresetKey] = useState(initialFilter || "none");
  const [sticker, setSticker] = useState(initialSticker || "none");
  const [makeup, setMakeup] = useState(initialMakeup || "none");
  const [mirrored, setMirrored] = useState(initialMirror);
  const [facing, setFacing] = useState("user");
  const [showFilters, setShowFilters] = useState(false);
  const [micOn, setMicOn] = useState(!startMicMuted);
  const stateRef = useRef({ params: { ...BEAUTY_DEFAULTS, ...(initialBeauty || {}) }, presetKey: initialFilter || "none", sticker: initialSticker || "none", makeup: initialMakeup || "none" });
  const pipeRef = useRef({});

  const pickParams = (p) => {
    stateRef.current.params = p;
    setParams(p);
  };
  const pickPreset = (key) => {
    stateRef.current.presetKey = key;
    setPresetKey(key);
  };
  const pickSticker = (key) => {
    stateRef.current.sticker = key;
    setSticker(key);
  };
  const pickMakeup = (key) => {
    stateRef.current.makeup = key;
    setMakeup(key);
  };

  useEffect(() => {
    if (!isHost) return;
    const t = setTimeout(() => {
      api.patch("/users/me/beauty-prefs", { params, preset: presetKey, sticker, makeup, mirror: mirrored }).catch((e) => console.warn("Beauty prefs autosave failed", e));
    }, 1200);
    return () => clearTimeout(t);
  }, [params, presetKey, sticker, makeup, mirrored, isHost]);

  const toggleMic = async () => {
    const t = pipeRef.current.micTrack;
    if (!t) return;
    const next = !micOn;
    try {
      await t.setMuted(!next);
      setMicOn(next);
    } catch (e) { /* ignore */ }
  };

  const flipCamera = async () => {
    const p = pipeRef.current;
    if (!p.AgoraRTC || !p.videoEl) return;
    const next = facing === "user" ? "environment" : "user";
    try {
      const newCam = await p.AgoraRTC.createCameraVideoTrack({ facingMode: { exact: next } })
        .catch(() => p.AgoraRTC.createCameraVideoTrack({ facingMode: next }));
      if (p.rawCam) {
        p.rawCam.stop();
        p.rawCam.close();
      }
      p.rawCam = newCam;
      p.videoEl.srcObject = new MediaStream([newCam.getMediaStreamTrack()]);
      await p.videoEl.play().catch(() => {});
      setFacing(next);
    } catch (e) {
      toast.error(next === "environment" ? "No back camera found on this device" : "Could not switch camera");
    }
  };

  useEffect(() => {
    if (!agora) return;
    let client, micTrack, camTrack;
    let cancelled = false;
    const p = pipeRef.current;
    // Capture the lock synchronously: our own cleanup will later replace
    // joinLock with a promise chained on THIS setup, awaiting the live
    // variable inside setup would deadlock on itself.
    const prevLock = joinLock;

    const setup = async () => {
      try {
        const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
        AgoraRTC.setLogLevel(3);
        p.AgoraRTC = AgoraRTC;

        if (isHost) {
          // Request mic + camera FIRST so the browser permission prompt always appears.
          try {
            micTrack = await AgoraRTC.createMicrophoneAudioTrack();
            if (startMicMuted) await micTrack.setMuted(true);
            p.micTrack = micTrack;
            p.rawCam = await AgoraRTC.createCameraVideoTrack({ facingMode: "user" });
          } catch (permErr) {
            console.error("Media permission error", permErr);
            setStatus(isPermissionError(permErr) ? "denied" : "error");
            return;
          }
          if (cancelled) {
            [micTrack, p.rawCam].forEach((t) => { try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ } });
            p.rawCam = null;
            return;
          }

          // WebGL pipeline: raw camera -> beauty/filter shader canvas -> published custom track.
          const videoEl = document.createElement("video");
          videoEl.muted = true;
          videoEl.playsInline = true;
          videoEl.srcObject = new MediaStream([p.rawCam.getMediaStreamTrack()]);
          await videoEl.play().catch(() => {});
          p.videoEl = videoEl;

          const pipeline = createBeautyPipeline(videoEl, () => stateRef.current);
          p.pipeline = pipeline;

          const canvasTrack = pipeline.canvas.captureStream(24).getVideoTracks()[0];
          camTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: canvasTrack });
          p.canvasTrack = canvasTrack;

          if (containerRef.current) camTrack.play(containerRef.current, { fit: "cover" });
          setStatus("live");
        }

        client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
        await client.setClientRole(isHost ? "host" : "audience");

        client.on("user-published", async (user, mediaType) => {
          await client.subscribe(user, mediaType);
          if (mediaType === "video" && containerRef.current && user.videoTrack) {
            user.videoTrack.play(containerRef.current, { fit: "cover" });
            setStatus("live");
          }
          if (mediaType === "audio" && user.audioTrack) user.audioTrack.play();
        });
        client.on("user-unpublished", () => { if (!isHost) setStatus("waiting"); });

        // Wait for any previous client (StrictMode remount) to fully leave
        // before joining with the same UID, preventing UID_CONFLICT.
        await prevLock.catch(() => { /* ignore */ });
        if (cancelled) return;
        await client.join(agora.app_id, agora.channel, agora.token, agora.uid);
        if (cancelled) {
          await client.leave().catch(() => { /* ignore */ });
          return;
        }

        if (isHost) {
          await client.publish([micTrack, camTrack]);
        } else {
          // Subscribe to anyone already publishing (covers cases where the
          // user-published event for existing publishers is missed).
          for (const u of client.remoteUsers) {
            if (u.hasVideo) {
              try {
                await client.subscribe(u, "video");
                if (containerRef.current && u.videoTrack) {
                  u.videoTrack.play(containerRef.current);
                  setStatus("live");
                }
              } catch (se) { /* ignore */ }
            }
            if (u.hasAudio) {
              try {
                await client.subscribe(u, "audio");
                u.audioTrack && u.audioTrack.play();
              } catch (se) { /* ignore */ }
            }
          }
          setStatus(client.remoteUsers.length ? "live" : "waiting");
        }
      } catch (e) {
        console.error("Agora error", e);
        setStatus(isPermissionError(e) ? "denied" : "error");
      }
    };
    const setupPromise = setup();

    return () => {
      cancelled = true;
      try { p.pipeline && p.pipeline.stop(); } catch (e) { /* ignore */ }
      joinLock = setupPromise
        .catch(() => { /* ignore */ })
        .then(() => (client ? client.leave().catch(() => { /* ignore */ }) : undefined));
      [micTrack, camTrack, p.rawCam].forEach((t) => {
        try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ }
      });
      try { p.canvasTrack && p.canvasTrack.stop(); } catch (e) { /* ignore */ }
      p.rawCam = null;
      p.videoEl = null;
      p.micTrack = null;
      p.pipeline = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agora, isHost, retryKey]);

  const beautyActive = Object.values(params).some((v) => v > 0) || presetKey !== "none" || sticker !== "none" || makeup !== "none";
  const selfMirror = isHost && mirrored && facing === "user";

  return (
    <div className={frameStyle ? "" : "absolute inset-0 bg-[#0E0E12]"} style={frameStyle || undefined}>
      <div ref={containerRef}
        className={`w-full h-full [&_video]:object-cover ${selfMirror ? "[&_video]:[transform:scaleX(-1)]" : ""}`}
        data-testid="agora-player" />

      {isHost && status === "live" && !frameStyle && (
        <>
          <div className="absolute right-3 top-32 z-30 flex flex-col gap-2.5">
            <button data-testid="flip-camera-button" onClick={flipCamera}
              className="glass-dark rounded-full w-10 h-10 flex items-center justify-center text-white active:scale-90 transition-transform">
              <SwitchCamera size={18} />
            </button>
            <button data-testid="host-mic-button" onClick={toggleMic}
              className={`glass-dark rounded-full w-10 h-10 flex items-center justify-center active:scale-90 transition-transform ${micOn ? "text-white" : "text-red-400"}`}>
              {micOn ? <Mic size={18} /> : <MicOff size={18} />}
            </button>
            <button data-testid="filters-button" onClick={() => setShowFilters((s) => !s)}
              className={`glass-dark rounded-full w-10 h-10 flex items-center justify-center active:scale-90 transition-transform ${showFilters || beautyActive ? "text-pink-400" : "text-white"}`}>
              <Wand2 size={18} />
            </button>
          </div>

          {showFilters && (
            <div className="absolute bottom-[8.5rem] left-0 right-0 z-30 px-3" data-testid="filters-tray">
              <BeautyTray
                params={params} onParams={pickParams}
                presetKey={presetKey} onPreset={pickPreset}
                sticker={sticker} onSticker={pickSticker}
                makeup={makeup} onMakeup={pickMakeup}
                mirrored={mirrored} onMirror={() => setMirrored((m) => !m)}
              />
            </div>
          )}
        </>
      )}

      {status !== "live" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-8 text-center">
          {status === "denied" ? (
            <div data-testid="permission-denied-panel">
              <p className="text-3xl mb-2">🎥🔒</p>
              <p className="text-white text-sm font-bold">Camera & microphone blocked</p>
              <p className="text-zinc-400 text-xs mt-1.5">Allow Camera and Microphone for this site in your browser settings (tap the lock icon near the address bar), then retry.</p>
              <button data-testid="permission-retry-button" onClick={() => { setStatus("connecting"); setRetryKey((k) => k + 1); }}
                className="mt-4 gradient-primary text-white text-xs font-bold px-6 py-2.5 rounded-full active:scale-95 transition-transform">
                Retry
              </button>
            </div>
          ) : status === "error" ? (
            <div data-testid="agora-error-panel">
              <p className="text-zinc-300 text-sm font-semibold">Could not connect to video</p>
              <p className="text-zinc-500 text-xs mt-1">Check your internet connection and try again.</p>
              <button data-testid="agora-retry-button" onClick={() => { setStatus("connecting"); setRetryKey((k) => k + 1); }}
                className="mt-4 glass-dark text-white text-xs font-bold px-6 py-2.5 rounded-full active:scale-95 transition-transform">
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" />
              <p className="text-zinc-400 text-xs">{status === "waiting" ? "Waiting for host video…" : "Connecting…"}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
